import * as React from 'react';
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { ErrorState } from '@/components/layout/error-state';
import { cn } from '@/lib/utils';
import { TablePagination, type TablePaginationProps } from './table-pagination';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  isLoading?: boolean;
  error?: Error | null;
  emptyState?: React.ReactNode;
  onRetry?: () => void;
  pagination?: Omit<TablePaginationProps, 'isLoading'>;
  /** 行的稳定标识。不给则退化成下标，插入一行会让其后每行的 DOM 被复用成别人的数据。 */
  getRowId?: (row: TData) => string;
}

// 列表恒定行数:加载骨架与空行填充都用它,保持翻页时表格高度稳定
const PLACEHOLDER_ROW_COUNT = 10;
// 操作列吸附右侧的公共样式(各处再叠加各自的层级与底色)
const STICKY_ACTIONS = 'sticky right-0 border-l shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.1)]';

export function DataTable<TData, TValue>({
  columns,
  data,
  isLoading,
  error,
  emptyState,
  onRetry,
  pagination,
  getRowId,
}: DataTableProps<TData, TValue>) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    ...(getRowId ? { getRowId } : {}),
  });

  if (error) {
    return (
      <div className="rounded-md border border-destructive/20 bg-destructive/5 py-12">
        <ErrorState message={error.message || '加载数据失败'} onRetry={onRetry} />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col space-y-4">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border bg-card shadow-[0_1px_3px_rgba(0,0,0,0.05)]">
        <Table wrapperClassName="flex-1 min-h-0">
          <TableHeader className="sticky top-0 z-20 border-b bg-muted/90 backdrop-blur-md">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent">
                {headerGroup.headers.map((header) => {
                  const isActions = header.id === 'actions';
                  return (
                    <TableHead
                      key={header.id}
                      className={cn(
                        'h-[var(--table-head-height)] whitespace-nowrap px-4 py-0 text-[0.6875rem] font-bold uppercase tracking-[0.05em] text-muted-foreground/90 md:text-[0.75rem]',
                        isActions && cn(STICKY_ACTIONS, 'z-30 bg-muted/90'),
                        header.column.columnDef.meta?.headerClassName,
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: PLACEHOLDER_ROW_COUNT }).map((_, i) => (
                <TableRow
                  key={i}
                  className="h-[var(--table-row-height)] border-b last:border-0 hover:bg-transparent"
                >
                  {columns.map((_, j) => (
                    <TableCell key={j} className="px-4 py-2">
                      <Skeleton className="h-4 w-full opacity-30" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows?.length ? (
              <>
                {table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    className="group relative h-[var(--table-row-height)] border-b transition-colors last:border-0 hover:bg-muted/40"
                  >
                    {row.getVisibleCells().map((cell) => {
                      const isActions = cell.column.id === 'actions';
                      return (
                        <TableCell
                          key={cell.id}
                          className={cn(
                            'px-4 py-0',
                            isActions &&
                              cn(
                                STICKY_ACTIONS,
                                'z-10 bg-card transition-colors group-hover:bg-muted/60',
                              ),
                            cell.column.columnDef.meta?.cellClassName,
                          )}
                        >
                          <div className="flex min-h-[var(--table-row-height)] items-center">
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </div>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
                {/* 填充空白行以保持页面视觉平衡 */}
                {Array.from({
                  length: Math.max(0, PLACEHOLDER_ROW_COUNT - table.getRowModel().rows.length),
                }).map((_, i) => (
                  <TableRow
                    key={`empty-${i}`}
                    className="h-[var(--table-row-height)] border-b border-muted/20 last:border-0 hover:bg-transparent"
                  >
                    {columns.map((_, j) => {
                      const isActions = columns[j]?.id === 'actions';
                      return (
                        <TableCell
                          key={j}
                          className={cn(
                            'px-4 py-0',
                            isActions && cn(STICKY_ACTIONS, 'z-10 bg-card'),
                          )}
                        />
                      );
                    })}
                  </TableRow>
                ))}
              </>
            ) : (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={columns.length} className="h-96 text-center">
                  <div className="flex h-full items-center justify-center">
                    {emptyState || (
                      <div className="text-sm font-medium text-muted-foreground">暂无数据</div>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {pagination && <TablePagination {...pagination} isLoading={isLoading} />}
    </div>
  );
}

// Extend ColumnDef to support custom meta for alignment
declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData, TValue> {
    headerClassName?: string;
    cellClassName?: string;
  }
}
